import React, { useState, useEffect } from 'react';
import { searchPersons, findRelationshipPath } from '../utils/gedcomParser';

const RelationshipFinder = ({ gedcomData, onClose, preselectedPerson, onPersonSelect }) => {
  const [person1, setPerson1] = useState(preselectedPerson || null);
  const [person2, setPerson2] = useState(null);
  const [search1, setSearch1] = useState('');
  const [search2, setSearch2] = useState('');
  const [results1, setResults1] = useState([]);
  const [results2, setResults2] = useState([]);
  const [relationshipPath, setRelationshipPath] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (preselectedPerson) {
      setSearch1(preselectedPerson.name);
    }
  }, [preselectedPerson]);

  const handleSearch1 = (term) => {
    setSearch1(term);
    if (term.length > 1) {
      const results = searchPersons(gedcomData, term);
      setResults1(results);
    } else {
      setResults1([]);
    }
  };

  const handleSearch2 = (term) => {
    setSearch2(term);
    if (term.length > 1) {
      const results = searchPersons(gedcomData, term);
      setResults2(results);
    } else {
      setResults2([]);
    }
  };

  const selectPerson1 = (person) => {
    setPerson1(gedcomData.individuals[person.id]);
    setSearch1(person.name);
    setResults1([]);
  };

  const selectPerson2 = (person) => {
    setPerson2(gedcomData.individuals[person.id]);
    setSearch2(person.name);
    setResults2([]);
  };

  const findRelationship = async () => {
    if (!person1 || !person2) return;
    
    setLoading(true);
    try {
      const path = findRelationshipPath(gedcomData, person1.id, person2.id);
      setRelationshipPath(path);
    } catch (error) {
      console.error('Error finding relationship:', error);
      setRelationshipPath({ error: 'Could not find relationship path' });
    }
    setLoading(false);
  };

  const getRelationshipDescription = (path) => {
    if (!path || path.error) return null;
    
    if (path.length === 1) {
      return "Same person";
    }
    
    if (path.length === 2) {
      return "Direct relationship (parent/child or spouse)";
    }
    
    // Simple relationship descriptions
    const distance = path.length - 1;
    if (distance === 2) return "Siblings or Parent-Child";
    if (distance === 3) return "Grandparent-Grandchild or Uncle/Aunt-Nephew/Niece";
    if (distance === 4) return "Great-grandparent or First Cousins";
    if (distance === 5) return "Great-great-grandparent or First Cousins Once Removed";
    
    return `${distance - 1} degrees of separation`;
  };

  return (
    <div className="relationship-finder-modal">
      <div className="relationship-finder-content">
        <div className="relationship-finder-header">
          <h2><i className="fas fa-route"></i> Find Family Relationship</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <div className="relationship-finder-body">
          <div className="person-selectors">
            <div className="person-selector">
              <h3>First Person</h3>
              <div className="search-container">
                <input
                  type="text"
                  value={search1}
                  onChange={(e) => handleSearch1(e.target.value)}
                  placeholder="Search for first person..."
                />
                {results1.length > 0 && (
                  <div className="search-results">
                    {results1.map(person => (
                      <div
                        key={person.id}
                        className="search-result"
                        onClick={() => selectPerson1(person)}
                      >
                        {person.name} {person.birth && `(b. ${person.birth})`}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {person1 && (
                <div 
                  className="selected-person clickable-person"
                  onClick={() => onPersonSelect && onPersonSelect(person1)}
                  title={`Click to view ${person1.name}'s details`}
                >
                  <div className={`person-avatar ${person1.sex === 'M' ? 'male' : 'female'}`}>
                    <i className={`fas ${person1.sex === 'M' ? 'fa-mars' : 'fa-venus'}`}></i>
                  </div>
                  <div className="person-info">
                    <strong>
                      {person1.name}
                      <i className="fas fa-external-link-alt person-link-icon"></i>
                    </strong>
                    {person1.birth && <div>Born: {person1.birth}</div>}
                  </div>
                </div>
              )}
            </div>

            <div className="relationship-arrow">
              <i className="fas fa-arrow-right"></i>
            </div>

            <div className="person-selector">
              <h3>Second Person</h3>
              <div className="search-container">
                <input
                  type="text"
                  value={search2}
                  onChange={(e) => handleSearch2(e.target.value)}
                  placeholder="Search for second person..."
                />
                {results2.length > 0 && (
                  <div className="search-results">
                    {results2.map(person => (
                      <div
                        key={person.id}
                        className="search-result"
                        onClick={() => selectPerson2(person)}
                      >
                        {person.name} {person.birth && `(b. ${person.birth})`}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {person2 && (
                <div 
                  className="selected-person clickable-person"
                  onClick={() => onPersonSelect && onPersonSelect(person2)}
                  title={`Click to view ${person2.name}'s details`}
                >
                  <div className={`person-avatar ${person2.sex === 'M' ? 'male' : 'female'}`}>
                    <i className={`fas ${person2.sex === 'M' ? 'fa-mars' : 'fa-venus'}`}></i>
                  </div>
                  <div className="person-info">
                    <strong>
                      {person2.name}
                      <i className="fas fa-external-link-alt person-link-icon"></i>
                    </strong>
                    {person2.birth && <div>Born: {person2.birth}</div>}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="find-button-container">
            <button 
              className="find-relationship-btn"
              onClick={findRelationship}
              disabled={!person1 || !person2 || loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Finding Relationship...
                </>
              ) : (
                <>
                  <i className="fas fa-search"></i>
                  Find Relationship
                </>
              )}
            </button>
          </div>

          {relationshipPath && (
            <div className="relationship-result">
              {relationshipPath.error ? (
                <div className="no-relationship">
                  <i className="fas fa-exclamation-triangle"></i>
                  <h3>No Relationship Found</h3>
                  <p>These two people do not appear to be connected in the family tree.</p>
                </div>
              ) : (
                <div className="relationship-found">
                  <h3><i className="fas fa-link"></i> Relationship Found!</h3>
                  <div className="relationship-description">
                    <strong>{getRelationshipDescription(relationshipPath)}</strong>
                  </div>
                  
                  <div className="relationship-path">
                    <h4>Connection Path:</h4>
                    <div className="path-chain">
                      {relationshipPath.map((person, index) => (
                        <React.Fragment key={person.id}>
                          <div 
                            className="path-person clickable-person"
                            onClick={() => onPersonSelect && onPersonSelect(person)}
                            title={`Click to view ${person.name}'s details`}
                          >
                            <div className={`person-avatar small ${person.sex === 'M' ? 'male' : 'female'}`}>
                              <i className={`fas ${person.sex === 'M' ? 'fa-mars' : 'fa-venus'}`}></i>
                            </div>
                            <div className="person-name">
                              {person.name}
                              <i className="fas fa-external-link-alt person-link-icon"></i>
                            </div>
                            {person.birth && <div className="person-year">({person.birth})</div>}
                          </div>
                          {index < relationshipPath.length - 1 && (
                            <div className="path-arrow">
                              <i className="fas fa-arrow-right"></i>
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="relationship-finder-footer">
          <button className="action-btn close-btn-alt" onClick={onClose}>
            <i className="fas fa-times"></i>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RelationshipFinder;